import "./Dialog.css";
import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { CheckCircleIcon, ChevronLeftIcon, ChevronRightIcon, CloseIcon, UserCogIcon } from '../Icons/Icons';
import RootState from '../../../redux/states/root.state';
import { AppDispatch } from '../../../redux/store';
import { fetchDealerships } from '../../../redux/actions/dealership.actions';
import { fetchUserData } from '../../../redux/actions/user.actions';

interface DialogProps {
    open: boolean,
    setOpen: any,
}

const ImpersonateDialog: React.FC<DialogProps> = ({open,setOpen}) => {
    const [selection, setSelection] = useState('customer');
    const dispatch = useDispatch<AppDispatch>();
    const { data: dealerships, loading: dealershipsLoading, meta: dealershipsMeta } = useSelector((state: RootState) => state.dealerships);
    
    // Dealership selection state
    const [dealershipInput, setDealershipInput] = useState("");
    const [selectedDealershipId, setSelectedDealershipId] = useState<string | null>(null);
    const [dealershipPage, setDealershipPage] = useState(1);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    
    var impersonating = sessionStorage.getItem("impersonate");
    var impersonatingDealership = sessionStorage.getItem("impersonate_dealership");
    
    
      // Fetch dealerships when input or page changes
      useEffect(() => {
        if (dropdownOpen) {
          const query = `name=${encodeURIComponent(dealershipInput)}&page=${dealershipPage}`;
          dispatch(fetchDealerships(query));
        }
      }, [dealershipInput, dealershipPage, dropdownOpen, dispatch]);
      
      // Close dropdown on outside click
      useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
          if (
            inputRef.current &&
            !inputRef.current.contains(event.target as Node) &&
            !(event.target as HTMLElement).closest('.dealership-dropdown')
          ) {
            setDropdownOpen(false);
          }
        }
        if (dropdownOpen) {
          document.addEventListener('mousedown', handleClickOutside);
        } else {
          document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }, [dropdownOpen]);
    
      useEffect(() => {
        if (impersonating) {
            setSelection(impersonating);
        } else {
            setOpen(false);
        }
        
        // Load saved dealership selection
        if (impersonatingDealership) {
            // Find the dealership name for the saved ID
            const savedDealership = dealerships?.find((d: any) => d.id === impersonatingDealership);
            if (savedDealership) {
                setDealershipInput(savedDealership.name);
                setSelectedDealershipId(savedDealership.id);
            } else {
                setSelectedDealershipId(impersonatingDealership);
            }
        }
      }, [impersonating, impersonatingDealership, dealerships, setOpen]);

      // Call API when dialog opens
      useEffect(() => {
        if (open) {
            // Dialog opened, call API with current selection (defaults to customer)
            const currentRole = impersonating || 'customer';
            dispatch(fetchUserData({ impersonate_role: currentRole }));
        }
      }, [open, impersonating, dispatch]);
    

    const handleSelection = (e:any) => {
        let val = e.target.value;
        setSelection(val);
        sessionStorage.setItem("impersonate", val);
        
        // Clear dealership selection when switching to customer
        if (val === 'customer') {
            setSelectedDealershipId(null);
            setDealershipInput("");
            sessionStorage.removeItem('impersonate_dealership');
            window.dispatchEvent( new Event('impersonate_dealership_change') );
        }
        
        // Directly refetch user data with new impersonation role
        dispatch(fetchUserData({ impersonate_role: val }));
        window.dispatchEvent( new Event('impersonate') ); 
    }
    
    const handleDealershipSelection = (dealership: any) => {
        setDealershipInput(dealership.name);
        setSelectedDealershipId(dealership.id);
        setDropdownOpen(false);
        sessionStorage.setItem("impersonate_dealership", dealership.id);
        window.dispatchEvent( new Event('impersonate') );
        window.dispatchEvent( new Event('impersonate_dealership_change') );
    }

    const handleLeave = () => {
        setSelection('customer');
        sessionStorage.removeItem('impersonate');
        sessionStorage.removeItem('impersonate_dealership');
        setSelectedDealershipId(null);
        setDealershipInput("");
        setOpen(false);
        // Directly refetch user data without impersonation
        dispatch(fetchUserData());
        window.dispatchEvent( new Event('impersonate') );
        window.dispatchEvent( new Event('impersonate_dealership_change') );
    }

    const dealershipOptions = (dealerships || []).filter((d: any) => d && d.id && d.name);
    const hasNextPage = Boolean(dealershipsMeta && dealershipPage < dealershipsMeta.total_pages);

    return (
        <div className={`imp-bar ${open? `show` : `hidden`}`} role="region" aria-label="Impersonation">
            <span className="imp-badge"><UserCogIcon/> Viewing as</span>

            <label className="visually-hidden" htmlFor="role-select">Role</label>
            <select id="role-select" className="input imp-select" value={selection} onChange={handleSelection}>
                <option value="owner">Owner</option>
                <option value="dealer">Dealer</option>
                <option value="sales">Sales</option>
                <option value="technician">Technician</option>
                <option value="customer">Customer</option>
            </select>

            {selection !== 'customer' && (
              <div className="imp-dealer">
                <label className="visually-hidden" htmlFor="imp-dealer-input">Dealership (optional)</label>
                <input
                  id="imp-dealer-input"
                  ref={inputRef}
                  className="input"
                  type="search"
                  placeholder="Any dealership"
                  value={dealershipInput}
                  onFocus={() => setDropdownOpen(true)}
                  onChange={e => {
                    const value = e.target.value;
                    setDealershipInput(value);
                    setDealershipPage(1);

                    // If user clears the input, clear the selected dealership and close dropdown
                    if (value === "") {
                      setSelectedDealershipId(null);
                      setDropdownOpen(false);
                      sessionStorage.removeItem('impersonate_dealership');
                      window.dispatchEvent( new Event('impersonate') );
                      window.dispatchEvent( new Event('impersonate_dealership_change') );
                    } else {
                      setDropdownOpen(true);
                    }
                  }}
                  role="combobox"
                  aria-expanded={dropdownOpen}
                  aria-controls="imp-dealer-list"
                  autoComplete="off"
                />
                {dropdownOpen && (
                  <div className="dealership-dropdown imp-dealer-dropdown">
                    {dealershipsLoading ? (
                      <div className="imp-dealer-empty">Loading…</div>
                    ) : (
                      <>
                        {dealershipOptions.length === 0 ? (
                          <div className="imp-dealer-empty">No dealerships found</div>
                        ) : (
                          <ul className="imp-dealer-list" id="imp-dealer-list" role="listbox">
                            {dealershipOptions.map((d: any) => (
                              <li key={d.id} role="option" aria-selected={selectedDealershipId === d.id}>
                                <button
                                  type="button"
                                  className={`imp-dealer-option ${selectedDealershipId === d.id ? 'selected' : ''}`}
                                  onClick={() => handleDealershipSelection(d)}
                                >
                                  {d.name}
                                  {selectedDealershipId === d.id && <CheckCircleIcon/>}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="imp-dealer-pager">
                          <button
                            type="button"
                            className="btn btn-sm btn-ghost"
                            disabled={dealershipPage <= 1}
                            onClick={() => dealershipPage > 1 && setDealershipPage(dealershipPage - 1)}
                          >
                            <ChevronLeftIcon/> Previous
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-ghost"
                            disabled={!hasNextPage}
                            onClick={() => hasNextPage && setDealershipPage(dealershipPage + 1)}
                          >
                            Next <ChevronRightIcon/>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            <button type="button" className="btn btn-sm imp-exit" onClick={()=>handleLeave()}>
                <CloseIcon/> Exit
            </button>
        </div>
    )
};

export default ImpersonateDialog;